import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  Table,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useConfirm } from '../../../hooks/useConfirm';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { use_LessonsQuery } from '../../../types/planung-graphql-client-defs';
import { hexToColorOption } from '../../../utils/colorUtils';
import { useTimetableStore } from '../../TimetableGrid/TimetableProvider';
import { TimetableVersionTableType } from '../graphql/types';
import { createTimetableVersionTableColumns } from '../utils/TimetableVersionTableUtils';

type TimetableVersionTeachersTableProps = {
  versionUuid: string;
  onRowSelection: (rowData: TimetableVersionTableType, index: number) => void;
  onShowLessonClick: (uuid: string) => void;
  versionTeacherUuids: string[];
  onRemove: (uuids: string[]) => void;
};

export const TimetableVersionTeachersTable = observer(
  ({
    onRemove,
    versionTeacherUuids,
    versionUuid,
    onRowSelection,
    onShowLessonClick,
  }: TimetableVersionTeachersTableProps) => {
    const { t } = useTranslation();
    const store = useTimetableStore();
    const { sorting, saveSorting } = useColumnsSort('timetableVersionTeachersTable');
    const { rowSelection, onRowSelectionChange } = useDefaultSelecting({ 0: true });

    const { teacherData } = useLoadBasicData({ pause: !versionUuid });

    const teachers: TimetableVersionTableType[] = useMemo(() => {
      return (
        teacherData?.people
          .filter((s) => versionTeacherUuids.includes(s.uuid))
          .map((s) => {
            const { html, label } = hexToColorOption(s.timetableConfig?.color ?? '');

            return {
              color: {
                color: html,
                label,
              },
              count: 0,
              name: s.displayNameShort ?? s.displayName,
              shortName: s.shortName,
              uuid: s.uuid,
            };
          }) ?? []
      );
    }, [teacherData, versionTeacherUuids]);

    const columns = useMemo(() => createTimetableVersionTableColumns(t('persons.name') as string), [t]);

    const [{ data: lessonsData }] = use_LessonsQuery({
      variables: {
        where: {
          versions_SOME: { uuid: versionUuid },
        },
      },
    });

    const canBeRemoved = (uuids: string[]) => {
      const lesson = lessonsData?.lessons.find((l) => {
        const teacherUuids = l.teachersConnection.edges.map((e) => e.node.uuid);
        return teacherUuids.some((t) => uuids.includes(t));
      });
      return !lesson;
    };

    const { confirm, ConfirmationDialog } = useConfirm();

    const handleRemove = async (uuids: string[]) => {
      const res = await confirm({
        title: t('timetableVersion.removeSubjects', { count: uuids.length }),
        message: t('timetableVersion.removeSubjectsDescription', { count: uuids.length }),
      });
      if (res) {
        onRemove(uuids);
      }
    };
    const readOnly = store.readonly;

    return (
      <>
        <Table<TimetableVersionTableType>
          showBorderRadius
          showShadow
          canScroll
          minHeight={900}
          sorting={sorting}
          onSortingChange={saveSorting}
          columns={columns}
          data={teachers}
          lastColWidth='140px'
          lastCol={(row) => {
            return (
              <ButtonGroup>
                <Button hierarchy='secondary' onClick={() => onShowLessonClick(row.original.uuid)}>
                  {t('timetableVersion.showLessons')}
                </Button>
                <Dropdown
                  noPadding
                  trigger={<Button hierarchy='secondary' icon={<DotsHorizontalIcon className='small' />} />}
                >
                  <DropdownMenu
                    data={[
                      {
                        label: t('timetableVersion.editData'),
                        onClick: () => {
                          console.log('edit clicked');
                        },
                        disabled: true,
                      },
                      {
                        label: t('timetableVersion.test'),
                      },
                      {
                        label: t('timetableVersion.remove'),
                        type: 'default',
                        color: 'error',
                        disabled: !canBeRemoved([row.original.uuid]) || readOnly,
                        onClick: async () => {
                          await handleRemove([row.original.uuid]);
                        },
                      },
                    ]}
                  />
                </Dropdown>
              </ButtonGroup>
            );
          }}
          showRowHover
          onRowClick={(event, row) => {
            if (onRowSelectionChange) onRowSelectionChange({ [row.id]: true });
            onRowSelection(row.original, row.index);
            document.querySelector('#content')?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onRowSelectionChange={onRowSelectionChange}
          rowSelection={rowSelection}
          showSelected
        />
        <ConfirmationDialog />
      </>
    );
  },
);
